import { useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { formatDate } from '../utils/formatters';
import { IntransitBasePage } from './IntransitBasePage';
import { CopyTeamsButton } from '../components/CopyTeamsButton';

const INTERNAL_INFO_ROWS = 3;

function InternalInfo({ info }) {
  if (!info || info === 'False') return null;

  const lines = info.split('\n').filter(line => line.trim() !== '').slice(0, INTERNAL_INFO_ROWS);

  return (
    <div className="mt-1" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
      {lines.map((line, i) => (
        <div key={i} className="text-muted" title={line}>
          {line}
        </div>
      ))}
    </div>
  );
}

export function IntransitInternalPage() {
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Move',
        cell: (info) => (
            <>
              <a href={info.row.original.url} target="_blank" rel="noopener noreferrer">
                {info.getValue()}
              </a>
                <InternalInfo info={info.row.original.internal_info} />
            </>
        ),
      },
      {
        accessorKey: 'origin',
        header: 'Origin',
      },
      {
        id: 'contents',
        header: 'Contents',
        cell: (info) => (
            <>
                {info.row.original.moves.map((move) => <div key={move.id}><b>{move.product_uom_qty}</b> x <a href={move.product_url} target="_blank" rel="noopener noreferrer">{move.product_default_code}</a> <small>{move.product_name}</small></div>)}
                <CopyTeamsButton
                    teamsHeader={info.row.original.teams_header}
                    triggerNotification={(msg) => info.table.options.meta?.triggerNotification(msg)}
                    className="btn btn-link btn-sm p-0 ms-2 text-decoration-none"
                    style={{ fontSize: '0.8rem' }}
                />
            </>
        )
      },
      {
        accessorKey: 'carrier_tracking_ref',
        header: 'Tracking',
        cell: (info) => (
          info.row.original.carrier_tracking_url_manual ? (
            <a href={info.row.original.carrier_tracking_url_manual} target="_blank" rel="noopener noreferrer">
              {info.getValue() || 'Tracking'}
            </a>
          ) : (
            info.getValue() || '-'
          )
        ),
      },
      {
        accessorKey: 'scheduled_date',
        header: 'ETA',
        cell: (info) => (
          <span style={{ color: info.row.original.eta_css?.match(/color:\s*(#[0-9a-fA-F]+);/)?.[1] || 'inherit' }}>
            {info.getValue() ? formatDate(new Date(info.getValue()), 'P') : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'write_uid',
        header: 'Last modified by',
      },
      {
        accessorKey: 'write_date',
        header: 'Last modified',
        cell: (info) => info.getValue() ? formatDate(new Date(info.getValue()), 'P HH:mm') : '-',
      },
    ],
    []
  );

  const expandedContent = (data) => (
    <>
      {data.internal_info && data.internal_info !== 'False' && (
        <div className="mb-3 border rounded p-3 bg-white shadow-sm">
          <strong>Internal Info:</strong>
          <div style={{ whiteSpace: 'pre-wrap' }}>{data.internal_info}</div>
        </div>
      )}
      {data.moves?.length > 0 ? (
        <div>
          <h6 className="mb-2">Products</h6>
          <Table size="sm" bordered hover className="mt-2 mb-0">
            <thead className="table-light">
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th className="text-end">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {data.moves.map((move) => (
                <tr key={move.id}>
                  <td>{move.product_name}</td>
                  <td>{move.product_default_code}</td>
                  <td className="text-end">{move.product_uom_qty}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-muted">No products found for this move.</div>
      )}
    </>
  );

  const globalFilterFn = (row, columnId, filterValue) => {
    const search = filterValue.toLowerCase();
    const name = (row.original.name || '').toLowerCase();
    const origin = (row.original.to_so || '').toLowerCase();
    if (name.includes(search) || origin.includes(search)) return true;

    return (row.original.moves || []).some((move) => 
      (move.product_name || '').toLowerCase().includes(search) ||
      (move.product_default_code || '').toLowerCase().includes(search)
    );
  };

  return (
    <IntransitBasePage 
      mode="internal" 
      label="Internal Transfers" 
      columns={columns} 
      expandedContent={expandedContent}
      globalFilterFn={globalFilterFn}
      searchHelp="Search by Move, Origin (SO), or Product (Name/SKU)"
    />
  );
}
